/**
 * Camada 2 de 3 — SEMÂNTICAS.
 *
 * Aqui a cor ganha PAPEL. Componente de feature importa daqui e só daqui:
 * nunca escreve hexadecimal, nunca lê `cores.primitivas.ts`.
 *
 * O arquivo também exporta `paresDeContraste` — a lista, legível por
 * máquina, de todo par (tinta, fundo) que o produto de fato pinta. O teste
 * em `apps/web/src/tema/contraste.test.ts` percorre essa lista e falha o
 * build abaixo de 4.5:1 (texto) ou 3:1 (UI não-textual).
 *
 * A regra estrutural que faz isso funcionar: os dois hexes que reprovam
 * (`marca.secundaria` e `marca.apoio`) só aparecem em campo `fundo`. Nenhum token de `tinta`
 * ou de `borda` pode apontar para eles, e o teste de completude prova que
 * toda tinta e toda borda declarada aqui aparece em pelo menos um par.
 */

import {
  acento,
  erro,
  marca,
  neutro,
  pessego,
  rampaPrimaria,
  realce,
  rosa,
  sucesso,
  teal,
} from './cores.primitivas';

// ---------------------------------------------------------------- superfícies

/** O que fica ATRÁS. Nunca é tinta. */
export const superficie = {
  pagina: neutro.fundoDaPagina,
  cartao: neutro.superficie,
  linhaDeTabela: neutro.superficie,
  /** Cabeçalho de tabela e barras — separa sem pesar. */
  sutil: neutro.superficieSutil,
  /** Preenchimentos fortes, que recebem `texto.inverso`. */
  primaria: marca.primaria,
  acento: marca.acento,
  negativa: erro.base,
  positiva: sucesso.base,
  /**
   * Preenchimentos de marca que NÃO aceitam branco (2.56:1 e 1.65:1).
   * Recebem `texto.sobreTeal` e `texto.sobrePessego`.
   */
  teal: teal.preenchimento,
  pessego: pessego.preenchimento,
  /** Trecho grifado dentro de um texto. Recebe `texto.primario`. */
  realce: realce.marcaTexto,
} as const;

// --------------------------------------------------------------------- tintas

/** O que fica NA FRENTE. Nunca é fundo. */
export const texto = {
  /** Leitura longa. */
  primario: neutro.tintaForte,
  /** Rótulo, metadado, coluna secundária. */
  secundario: neutro.tintaMedia,
  /** Desabilitado. Medido mesmo assim — a WCAG isenta, a nossa régua não. */
  desabilitado: neutro.tintaFraca,
  /** Sobre superfície forte. */
  inverso: neutro.tintaInversa,
  /** Link e ação primária em texto. */
  link: marca.primaria,
  acento: marca.acento,
  positivo: sucesso.base,
  negativo: erro.base,
  /** Teal legível — NÃO é `marca.secundaria`. */
  teal: teal.texto,
  /** Pêssego legível — NÃO é `marca.apoio`. */
  pessego: pessego.texto,
  /** A única tinta permitida sobre `superficie.teal`. */
  sobreTeal: teal.tinta,
  /** A única tinta permitida sobre `superficie.pessego`. */
  sobrePessego: pessego.tinta,
} as const;

// --------------------------------------------------------------------- bordas

export const borda = {
  /** Separador decorativo. Não carrega informação; sem mínimo de contraste. */
  sutil: neutro.separador,
  /** Contorno de campo e de controle. Carrega informação: mínimo 3:1. */
  forte: neutro.tintaFraca,
  /** Anel de foco. `outline: none` é proibido no tema. */
  foco: marca.primaria,
  teal: teal.borda,
  pessego: pessego.borda,
  negativa: erro.base,
  positiva: sucesso.base,
} as const;

// ------------------------------------------------------- tokens de domínio

/** Um estado pintável: preenchimento, a tinta que vai sobre ele, e a borda. */
export interface TomDeEstado {
  readonly fundo: string;
  readonly tinta: string;
  readonly borda: string;
}

/**
 * SLA. O relógio do caso.
 * `atencao` usa o pêssego cheio porque é exatamente o papel dele: superfície.
 */
/**
 * Rampa para gráfico, do mais claro ao mais escuro. Leia como uma escala:
 * a posição na rampa É a grandeza. NÃO use como paleta categórica — para
 * isso faltariam hues, e os que sobram na marca são cores de estado.
 */
export const grafico = {
  rampa: [
    rampaPrimaria.passo1,
    rampaPrimaria.passo2,
    rampaPrimaria.passo3,
    rampaPrimaria.passo4,
  ],
  /** Série única: quando não há escala, há uma cor só. */
  serie: rampaPrimaria.passo3,
  /** Grade e eixo. Recessivos por definição. */
  grade: neutro.separador,
  eixo: neutro.tintaFraca,
} as const;

export const sla = {
  ok: { fundo: sucesso.claro, tinta: sucesso.tinta, borda: sucesso.base },
  atencao: { fundo: pessego.preenchimento, tinta: pessego.tinta, borda: pessego.borda },
  estourado: { fundo: erro.claro, tinta: erro.tinta, borda: erro.base },
  semPrazo: { fundo: neutro.chip, tinta: neutro.tintaDeChip, borda: neutro.tintaFraca },
} as const satisfies Record<string, TomDeEstado>;

/**
 * Fila de execução. É o que deixa o robô visível.
 * `processando` usa o teal cheio — de novo, superfície com tinta própria.
 */
export const fila = {
  ociosa: { fundo: neutro.chip, tinta: neutro.tintaDeChip, borda: neutro.tintaFraca },
  processando: { fundo: teal.claro, tinta: teal.tinta, borda: teal.borda },
  pausada: { fundo: pessego.claro, tinta: pessego.texto, borda: pessego.borda },
  erro: { fundo: erro.claro, tinta: erro.tinta, borda: erro.base },
  dlq: { fundo: acento.claro, tinta: acento.tinta, borda: acento.base },
} as const satisfies Record<string, TomDeEstado>;

/**
 * Esteira da captação: corre sozinho · espera pessoa · barrado.
 * `barrado` não é `fila.erro` — uma se resolve com reprocesso, a outra com recurso.
 */
export const esteira = {
  correSozinho: { fundo: teal.claro, tinta: teal.texto, borda: teal.borda },
  esperaPessoa: { fundo: acento.claro, tinta: acento.tinta, borda: acento.base },
  barrado: { fundo: rosa.claro, tinta: rosa.tinta, borda: rosa.base },
} as const satisfies Record<string, TomDeEstado>;

export type ChaveDeSla = keyof typeof sla;
export type ChaveDeFila = keyof typeof fila;
export type ChaveDeEsteira = keyof typeof esteira;

// ------------------------------------------------------- pares de contraste

export type TipoDeContraste = 'texto' | 'ui';

export interface ParDeContraste {
  /** Nome legível — é o que aparece quando o teste falha. */
  readonly nome: string;
  readonly tinta: string;
  readonly fundo: string;
  /** `texto` exige 4.5:1; `ui` (borda, ícone, controle) exige 3:1. */
  readonly tipo: TipoDeContraste;
}

/** Os fundos claros sobre os quais qualquer tinta pode cair. */
const FUNDOS_CLAROS = [
  ['página', superficie.pagina],
  ['cartão', superficie.cartao],
  ['cabeçalho', superficie.sutil],
] as const;

/**
 * Tintas que podem cair sobre qualquer fundo claro.
 * `inverso`, `sobreTeal` e `sobrePessego` ficam de fora: têm superfície
 * própria, e são medidas contra ela logo abaixo.
 */
const TINTAS_SOBRE_CLARO = [
  ['texto.primario', texto.primario],
  ['texto.secundario', texto.secundario],
  ['texto.desabilitado', texto.desabilitado],
  ['texto.link', texto.link],
  ['texto.acento', texto.acento],
  ['texto.positivo', texto.positivo],
  ['texto.negativo', texto.negativo],
  ['texto.teal', texto.teal],
  ['texto.pessego', texto.pessego],
] as const;

/** Bordas e ícones que podem cair sobre qualquer fundo claro. */
const BORDAS_SOBRE_CLARO = [
  ['borda.forte', borda.forte],
  ['borda.foco', borda.foco],
  ['borda.teal', borda.teal],
  ['borda.pessego', borda.pessego],
  ['borda.negativa', borda.negativa],
  ['borda.positiva', borda.positiva],
] as const;

/** Superfícies fortes, que só recebem `texto.inverso`. */
const SUPERFICIES_FORTES = [
  ['superficie.primaria', superficie.primaria],
  ['superficie.acento', superficie.acento],
  ['superficie.negativa', superficie.negativa],
  ['superficie.positiva', superficie.positiva],
] as const;

/**
 * Superfícies de marca com tinta própria. É aqui que a derivação obrigatória
 * do briefing é exercida: o preenchimento que reprova para texto recebe a
 * tinta que passa, e o par é medido como qualquer outro.
 */
const SUPERFICIES_DE_MARCA = [
  ['superficie.teal', superficie.teal, texto.sobreTeal],
  ['superficie.pessego', superficie.pessego, texto.sobrePessego],
] as const;

function paresDeEstado(grupo: string, estados: Record<string, TomDeEstado>): ParDeContraste[] {
  return Object.entries(estados).flatMap(([chave, tom]) => [
    {
      nome: `${grupo}.${chave} — tinta sobre preenchimento`,
      tinta: tom.tinta,
      fundo: tom.fundo,
      tipo: 'texto' as const,
    },
    { nome: `${grupo}.${chave} — borda sobre página`, tinta: tom.borda, fundo: superficie.pagina, tipo: 'ui' as const },
    { nome: `${grupo}.${chave} — borda sobre cartão`, tinta: tom.borda, fundo: superficie.cartao, tipo: 'ui' as const },
  ]);
}

/**
 * Todos os pares que o produto realmente pinta.
 *
 * Note o que NÃO está aqui: `teal.preenchimento` e `pessego.preenchimento`
 * contra a página. Preenchimento é superfície — a coisa que o delimita
 * contra o fundo é `borda.teal` / `borda.pessego`, e essas estão na lista.
 */
export const paresDeContraste: readonly ParDeContraste[] = [
  ...FUNDOS_CLAROS.flatMap(([nomeDoFundo, fundo]) =>
    TINTAS_SOBRE_CLARO.map(([nomeDaTinta, tinta]) => ({
      nome: `${nomeDaTinta} sobre ${nomeDoFundo}`,
      tinta,
      fundo,
      tipo: 'texto' as const,
    })),
  ),
  ...FUNDOS_CLAROS.flatMap(([nomeDoFundo, fundo]) =>
    BORDAS_SOBRE_CLARO.map(([nomeDaBorda, tinta]) => ({
      nome: `${nomeDaBorda} sobre ${nomeDoFundo}`,
      tinta,
      fundo,
      tipo: 'ui' as const,
    })),
  ),
  ...SUPERFICIES_FORTES.map(([nomeDaSuperficie, fundo]) => ({
    nome: `texto.inverso sobre ${nomeDaSuperficie}`,
    tinta: texto.inverso,
    fundo,
    tipo: 'texto' as const,
  })),
  ...SUPERFICIES_DE_MARCA.map(([nomeDaSuperficie, fundo, tinta]) => ({
    nome: `tinta própria sobre ${nomeDaSuperficie}`,
    tinta,
    fundo,
    tipo: 'texto' as const,
  })),
  { nome: 'texto.primario sobre superficie.realce', tinta: texto.primario, fundo: superficie.realce, tipo: 'texto' },
  ...paresDeEstado('sla', sla),
  ...paresDeEstado('fila', fila),
  ...paresDeEstado('esteira', esteira),
];

/**
 * Toda tinta e toda borda declarada neste arquivo, achatada. O teste de
 * completude exige que cada uma apareça em `paresDeContraste` — é o que
 * impede um token novo de entrar sem ser medido.
 *
 * `borda.sutil` fica de fora por ser separador decorativo: não carrega
 * informação e a WCAG 1.4.11 não se aplica.
 */
export const tintasQueExigemPar: readonly string[] = [
  ...Object.values(texto),
  ...Object.entries(borda)
    .filter(([chave]) => chave !== 'sutil')
    .map(([, valor]) => valor),
  ...Object.values(sla).flatMap((t) => [t.tinta, t.borda]),
  ...Object.values(fila).flatMap((t) => [t.tinta, t.borda]),
  ...Object.values(esteira).flatMap((t) => [t.tinta, t.borda]),
];
